import {FC, useMemo} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import LinkButton from 'components/LinkButton';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import {useBackend} from 'state/backend';
import {toCurrency} from 'utils/number';
import MailpointImage from './MailpointImage';
import MailpointSelection from './MailpointSelection';
import PriceCalculation from './PriceCalculation';
import {MailpointsForm, MailpointsInitialData} from './types';

const Mailpoints: FC = () => {
    const {data} = useBackend<MailpointsInitialData>();

    const form = useForm<MailpointsForm>({
        defaultValues: {
            mailpoints: undefined,
            mode: 'presets',
        },
    });

    const {t} = useTranslation('Billing.Mailpoints');

    const [mailpoints, mode] = form.watch(['mailpoints', 'mode']);

    const multiple =
        mode === 'manual' && data.onlyMultiplesOfTenAllowed ? 10 : 1;

    const value = Number(mailpoints || 0) * multiple;
    const min = multiple === 10 ? data.mpPackMinimum : data.mpMinimum;
    const max = multiple === 10 ? data.mpPackMaximum : data.mpMaximum;

    const isValid = useMemo(() => {
        const mailpointsNumber = Number(mailpoints);

        return mailpointsNumber >= min && mailpointsNumber <= max;
    }, [mailpoints, max, min]);

    return (
        <FormProvider {...form}>
            <NavigationTabs.Billing selection="buyMailPoints" />
            <div className="mx-auto mt-2 flex max-w-4xl flex-row gap-x-12 bg-green-10 p-4">
                <MailpointImage />
                <div className="min-w-[400px] flex-1">
                    <div className="mb-4 flex flex-col gap-y-2 rounded border border-grey-300 bg-grey-50 p-4">
                        <div>
                            {t('youCurrentlyHave', {
                                mailpoints: data.currentMp,
                            })}
                        </div>
                        <div>{t('buyAdditional')}</div>
                        <MailpointSelection />
                        <div className="text-sm text-grey-700">
                            {t('pricePer', {
                                price: toCurrency({
                                    currency: 'JPY',
                                    value: data.mpUnitPrice,
                                }),
                            })}
                        </div>
                    </div>
                    <div className="grid">
                        <PriceCalculation
                            isValid={isValid}
                            mailpoints={value}
                            unitPrice={data.mpUnitPrice}
                        />
                        <LinkButton
                            aria-label="submit"
                            className="w-full rounded-t-none text-center"
                            disabled={!isValid}
                            href={window.Urls.checkout_mailpoints_view(
                                `${value}`,
                            )}
                            kind="primary"
                            size="lg"
                        >
                            {isValid ? (
                                t('submitButton.submit')
                            ) : (
                                <span className="text-sm">
                                    {t('submitButton.helperText', {
                                        max: data.mpMaximum,
                                        min: data.mpMinimum,
                                    })}
                                </span>
                            )}
                        </LinkButton>
                    </div>
                </div>
            </div>
        </FormProvider>
    );
};

export default Mailpoints;
